/**
 * ── Razorpay Checkout on the web ─────────────────────────────────────────
 *
 * The web build of `react-native-razorpay`. Nothing imports this file by name:
 * `metro.config.js` aliases the package to it for `platform === 'web'`, so the
 * `require('react-native-razorpay')` inside [[razorpayCheckout]] lands here
 * instead of on the native module.
 *
 * Razorpay ships web checkout as a SCRIPT, not a package, so the first job is
 * fetching `checkout.js` and reading the constructor it hangs off `window`. The
 * second — and the one that shapes this whole file — is that its API is
 * event-driven where the native SDK's is a promise: success arrives on
 * `handler`, dismissal on `modal.ondismiss`, failure on a `payment.failed`
 * subscription. Three callbacks in, one promise out.
 *
 * ── The rejection shape is the contract ─────────────────────────────────
 * [[razorpayCheckout]] is the single place that decides what a failed checkout
 * MEANS, and it reads rejections field by field because the native bridge's
 * declared types are wrong. This shim is held to the same contract, so what it
 * rejects with is deliberate, not incidental:
 *
 *   - `payment.failed` carries Razorpay's OWN error envelope under `error`,
 *     with the same `code`/`description`/`reason` fields the native bridge
 *     nests. It's passed through untouched at that same key, which is why
 *     descriptions, cancel reasons and pending reasons need no web-specific
 *     handling over there.
 *   - The other two outcomes have no native counterpart, so they carry the
 *     `webCheckout` tag below and are read explicitly.
 *
 * Rejecting with a plain object rather than an `Error` is also deliberate — it
 * matches what the native bridge does, and the one `catch` that sees it is
 * built to read exactly this.
 */

/**
 * The tag on every rejection from this file. Native never sets it, so it's also
 * how [[razorpayCheckout]] knows a rejection came from the web at all.
 *
 *   `cancelled`    the user dismissed the modal. Not an error.
 *   `failed`       Razorpay reported a payment failure; `error` holds its own
 *                  envelope, verbatim.
 *   `unavailable`  checkout never opened — the script didn't load. No sheet was
 *                  shown, so no payment was attempted.
 */
export type WebCheckoutRejection = {
  webCheckout: 'cancelled' | 'failed' | 'unavailable';
  /** Razorpay's error envelope. Present for `failed`, and only for `failed`. */
  error?: unknown;
  /** Whatever went wrong underneath, for the log. Never shown to the user. */
  cause?: unknown;
};

const SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

/**
 * Give up on the script fetch after this long.
 *
 * [[razorpayCheckout]] explains why the OPEN SHEET must never be put on a timer
 * — a user may legitimately sit on it for minutes, so any timeout long enough
 * to be safe is too long to help. None of that applies here: nothing is on
 * screen yet and the user is staring at a spinning button. A `<script>` whose
 * `onload`/`onerror` never fire (a stalled proxy, a captive portal that
 * black-holes the request) would otherwise leave that button in flight forever.
 */
const SCRIPT_TIMEOUT_MS = 15_000;

type RazorpayInstance = {
  open: () => void;
  close: () => void;
  on: (event: 'payment.failed', handler: (response: unknown) => void) => void;
};

type RazorpayConstructor = new (
  options: Record<string, unknown>
) => RazorpayInstance;

/** The constructor `checkout.js` hangs off `window`, or undefined pre-load. */
const readGlobalRazorpay = (): RazorpayConstructor | undefined =>
  (window as unknown as { Razorpay?: RazorpayConstructor }).Razorpay;

const unavailable = (cause: unknown): WebCheckoutRejection => ({
  webCheckout: 'unavailable',
  cause,
});

/**
 * Fetch `checkout.js` once per page.
 *
 * The in-flight promise is cached so two presses of the pay button don't inject
 * two script tags — but only while it's PENDING or RESOLVED. A failed load
 * clears the cache (and drops the dead tag) so pressing pay again after the
 * connection comes back genuinely retries instead of replaying the failure.
 */
let loader: Promise<void> | null = null;

const loadCheckoutScript = (): Promise<void> => {
  // Server-rendered pass. Unreachable in practice — the only caller is a button
  // press — but this module is evaluated during SSR, so it must not assume DOM.
  if (typeof window === 'undefined') {
    return Promise.reject(
      unavailable(new Error('Razorpay Checkout needs a browser'))
    );
  }
  if (readGlobalRazorpay()) return Promise.resolve();
  if (loader) return loader;

  loader = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');

    let timer: ReturnType<typeof setTimeout> | undefined;
    const fail = (cause: unknown) => {
      clearTimeout(timer);
      loader = null;
      script.remove();
      reject(unavailable(cause));
    };

    timer = setTimeout(
      () =>
        fail(new Error(`Razorpay Checkout timed out loading ${SCRIPT_SRC}`)),
      SCRIPT_TIMEOUT_MS
    );

    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      clearTimeout(timer);
      // Loaded, but the global we need isn't there: an ad blocker or a
      // corporate proxy served a 200 with something that isn't checkout.js.
      // Treated as a failed load, because it is one.
      if (!readGlobalRazorpay()) {
        fail(new Error('Razorpay Checkout loaded without defining Razorpay'));
        return;
      }
      resolve();
    };
    script.onerror = () =>
      fail(new Error(`Razorpay Checkout failed to load from ${SCRIPT_SRC}`));

    document.body.appendChild(script);
  });

  return loader;
};

/** What `handler` is handed on success. Dropped by the caller — see below. */
type RazorpayResponse = {
  razorpay_payment_id: string;
  razorpay_order_id?: string;
  razorpay_subscription_id?: string;
  razorpay_signature: string;
};

const RazorpayCheckout = {
  /**
   * Open web checkout with the same options the native `open` takes.
   *
   * Resolves with Razorpay's success payload — which [[razorpayCheckout]]
   * throws away on purpose, since the capture webhook reaching our server, not
   * this callback, is what confirms a booking. It's returned anyway so this
   * stays a faithful stand-in for the native SDK.
   */
  open: async (options: Record<string, unknown>): Promise<RazorpayResponse> => {
    await loadCheckoutScript();

    return new Promise<RazorpayResponse>((resolve, reject) => {
      // Every outcome below can be followed by another one — a failure that
      // then dismisses, a dismissal fired by our own `close()`. First past the
      // post wins; the rest are noise.
      let settled = false;
      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        fn();
      };

      let rzp: RazorpayInstance | undefined;

      try {
        const Razorpay = readGlobalRazorpay();
        // Loaded a moment ago and gone now: something on the page tore it out.
        if (!Razorpay) {
          throw new Error('Razorpay Checkout disappeared after loading');
        }

        rzp = new Razorpay({
          ...options,

          // ── Why retry is forced off ──────────────────────────────────
          // Web checkout's default is to keep the modal OPEN after a failed
          // payment and offer a retry inside it. That breaks the one-attempt
          // contract this promise has: we reject on `payment.failed`, the
          // screen shows the error — and the modal is still sitting there. A
          // retry succeeding in it would fire `handler` on a promise that
          // settled long ago, so the user would have paid into a screen that
          // already told them it went wrong.
          //
          // Off, the modal closes with the failure and the native flow is
          // restored exactly: one attempt, then out. Retrying is pressing the
          // button again, which resumes the SAME booking on the SAME order via
          // the confirm screen's idempotency key.
          retry: { enabled: false },

          handler: (response: RazorpayResponse) => {
            settle(() => resolve(response));
          },

          modal: {
            ...(typeof options.modal === 'object' && options.modal !== null
              ? options.modal
              : {}),
            ondismiss: () => {
              settle(() =>
                reject({
                  webCheckout: 'cancelled',
                } satisfies WebCheckoutRejection)
              );
            },
          },
        });

        rzp.on('payment.failed', (response: unknown) => {
          settle(() =>
            reject({
              webCheckout: 'failed',
              // Razorpay's envelope, untouched and at the key the caller
              // already reads for native. Handing over the whole response if
              // the shape ever surprises us is the point.
              error:
                typeof response === 'object' && response !== null
                  ? (response as { error?: unknown }).error
                  : undefined,
            } satisfies WebCheckoutRejection)
          );
          // Belt and braces with `retry.enabled: false`: never leave a modal
          // stranded on top of a screen that has already moved on.
          closeQuietly(rzp);
        });

        rzp.open();
      } catch (cause) {
        // `new Razorpay(...)` and `open()` can both throw synchronously — bad
        // options, or a script that isn't the one we asked for. Nothing opened,
        // so this is the same outcome as a load failure.
        settle(() => reject(unavailable(cause)));
      }
    });
  },
};

/**
 * Close the modal, ignoring the throw.
 *
 * The only reason `close()` fails is that checkout has already closed itself,
 * which is the state we were asking for. Nothing is being swallowed: the
 * outcome this ran alongside has already been rejected to the caller.
 */
function closeQuietly(rzp: RazorpayInstance | undefined) {
  try {
    rzp?.close();
  } catch {
    // Already closed.
  }
}

export default RazorpayCheckout;
